/**
 * Utilties for sending events to Google Tag Manager (GTM).
 *
 * Marketing uses GTM events to share user behavior with sites we advertise on.
 * Some of the reasons we may share this information with advertises are to
 * register ad conversions (when the advertiser does not believe the user is
 * under 13) and to create lookalike audiances (when the advertiser knows the
 * user is over 18).
 *
 * The rules which specify how these events are used are configured at
 * https://tagmanager.google.com
 */

export type SignupRole = "Teacher" | "Parent" | "Student";
type ActionType =
    | "Video-Start"
    | "Article-View"
    | "Scratchpad-View"
    | "Problem-Attempt";

/**
 * Sent when a coach adds any positive integer of students.
 */
interface StudentsAddedEvent {
    event: "studentsAdded";
    totalStudentsIncludingPending: number;
    countStudentsJustAdded: number;
}

/**
 * Sent when a coach adds the tenth student.
 */
interface TenthStudentAddedEvent {
    event: "tenthStudentAdded";
}

/**
 * Sent when a user first logs in.
 */
interface SignupCompleteEvent {
    event: "signupComplete";
    role: SignupRole | null | undefined;
}

/**
 * Sent when a user logs in.
 */
interface ReturningLoginEvent {
    event: "returningLogin";
    role: SignupRole | null | undefined;
}

/**
 * Sent when a user views a video, an article, or uses the scratchpad.
 */
interface LearningActionEvent {
    event: "learningAction";
    actionType: ActionType;
    slug: string | null | undefined;
}

/**
 * Sent when a user gives permission to share data with CollegeBoard.
 */
interface CollegeBoardDataPermissionsEvent {
    event: "SAT-CollegeBoard-Link";
}

// Add new events above and to the GTMEvent union below.
// You do not need to register this event anywhere else in webapp to use it.
//
// Once these events are deployed, the marketing team can use them by creating
// a custom trigger with type "Custom Event" and the event name specified
// in https://tagmanager.google.com.
//
// Any variable other than "event" in these events is a global variable that
// can be used in GTM by creating a variable with type "Data Layer Variable".
// It's important to note that these values will be overwritten if another
// variable with that name is pushed in an event.
type GTMEvent =
    | StudentsAddedEvent
    | TenthStudentAddedEvent
    | SignupCompleteEvent
    | ReturningLoginEvent
    | LearningActionEvent
    | CollegeBoardDataPermissionsEvent;

// dataLayer is the GTM's API.
// See https://developers.google.com/tag-manager/devguide#adding-data-layer-variables-to-a-page
// @ts-expect-error - TS2339 - Property 'dataLayer' does not exist on type 'Window & typeof globalThis'. | TS2339 - Property 'dataLayer' does not exist on type 'Window & typeof globalThis'.
window.dataLayer = window.dataLayer || [];

/**
 * If GTM has been loaded, this sends an event to GTM.
 *
 * You must not use this to send an event that could be directed to children if
 * you are not sure whether the user is under 13 or not.
 *
 * Things like donating money or adding a student to a class are not
 * child-directed, but anything to do with learning is, if the user has not
 * provided a birthdate that shows that they are over 13.
 *
 * This has an obnoxious name to remind us to follow the law.
 */
export function sendAnEventThatIsNotChildDirectedToAdvertisers(
    event: GTMEvent,
) {
    // @ts-expect-error - TS2339 - Property 'dataLayer' does not exist on type 'Window & typeof globalThis'.
    window.dataLayer.push(event);
}

/**
 * If GTM has been loaded, this sends an event to GTM. From GTM,
 * the event is sent to GA4.
 *
 * We can track events for use in Google Optimize or GA4.
 * creating a less-confusing name here. The above method does
 * the same thing, but was directed initally at advertisers like
 * Facebook, for instance.
 *
 */
export function sendAnEventToGA4(event: GTMEvent) {
    // @ts-expect-error - TS2339 - Property 'dataLayer' does not exist on type 'Window & typeof globalThis'.
    window.dataLayer.push(event);
}
